import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import RestaurantHallsPayrollPanel, { RestaurantHallsReview } from '../../components/RestaurantHallsPayrollPanel';
import { OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE, useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';
import { isRestaurantOperatingContext } from '../../utils/operatingContext';
import { formatRestaurantPayrollDecimal } from '../../utils/restaurantPayrollFormat';
import './SalaryPayrollPage.css';

type PayrollEmployee = {
  employmentId: number;
  employeeName: string;
  departmentName: string;
  position: string;
  method: 'fixed-plus-revenue' | 'hourly-coefficient' | 'revenue-weighted-hours' | 'halls-fund';
  workedHours: number;
  hourlyRate: number;
  evaluationCoefficient: number;
  evaluationCoefficientSource: string;
  salaryHours: number;
  baseSalary: number;
  fixedBonus: number;
  bonusPercent: number;
  bonus: number;
  fixedPenalty: number;
  penaltyPercent: number;
  penalty: number;
  fixedMonthlyRate: number;
  revenuePercent: number;
  monthlyRevenue: number;
  amount: number;
  policySource: string;
  policyEffectiveYear: number;
  policyEffectiveMonth: number;
  adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
};

type PayrollPeriod = {
  exists: boolean;
  year: number;
  month: number;
  status: 'draft' | 'approved' | 'closed';
  version: number;
  monthlyRevenue: number;
  salaryFundPercent: number;
  salaryFund: number;
  totalSalaryHours: number;
  hourRate: number;
  total: number;
  issues: string[];
  employees: PayrollEmployee[];
  halls?: RestaurantHallsReview | null;
  legacyHallsHistory?: {
    status: string;
    departmentNameSnapshot: string;
    orderRevenueSnapshot: number;
    totalPoolSnapshot: number;
    employees: Array<{ employeeName: string; amount: number }>;
  } | null;
  approvedAtUtc?: string;
  approvedByUsername?: string;
  closedAtUtc?: string;
  closedByUsername?: string;
};

type PayrollOptions = {
  employees: Array<{
    employmentId: number;
    fullName: string;
    position: string;
    departmentName: string;
    baseRate: number;
  }>;
  policies: Array<{
    id: number;
    scopeKey: string;
    employmentId?: number | null;
    effectiveYear: number;
    effectiveMonth: number;
    revision: number;
    calculationMethod: PayrollEmployee['method'];
    fixedMonthlyRate: number;
    revenuePercent: number;
    hourlyRateOverride?: number | null;
    evaluationCoefficientOverride?: number | null;
  }>;
  salaryRules: Array<{
    id: number;
    position: string;
    effectiveYear: number;
    effectiveMonth: number;
    revision: number;
    baseRate: number;
    isActive: boolean;
  }>;
  revenue?: { amount: number; notes: string; version: number } | null;
};

type DraftInputs = { fixedBonus: string; bonusPercent: string; fixedPenalty: string; penaltyPercent: string };

const currency = new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH' });
const DISCLOSURE_STORAGE_KEY = 'crm4chef:restaurant-payroll:sections:v1';
type DisclosureKey = 'halls' | 'revenue' | 'policies' | 'bonuses' | 'adjustments';
type DisclosureState = Record<DisclosureKey, boolean>;
const defaultDisclosureState: DisclosureState = {
  halls: false,
  revenue: false,
  policies: false,
  bonuses: false,
  adjustments: false,
};
const policyScopeLabels: Record<string, string> = { global: 'Усього ресторану' };
const calculationMethodLabels: Record<string, string> = {
  'fixed-plus-revenue': 'Фіксована сума + частка виторгу',
  'hourly-coefficient': 'Години × ставка × коефіцієнт',
  'revenue-weighted-hours': 'Фонд за зваженими годинами',
};
const readDisclosureState = (): DisclosureState => {
  const stored = localStorage.getItem(DISCLOSURE_STORAGE_KEY);
  if (!stored) return defaultDisclosureState;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) return defaultDisclosureState;
    const saved = parsed as Record<string, unknown>;
    return {
      halls: typeof saved.halls === 'boolean' ? saved.halls : false,
      revenue: typeof saved.revenue === 'boolean' ? saved.revenue : false,
      policies: typeof saved.policies === 'boolean' ? saved.policies : false,
      bonuses: typeof saved.bonuses === 'boolean' ? saved.bonuses : false,
      adjustments: typeof saved.adjustments === 'boolean' ? saved.adjustments : false,
    };
  } catch {
    return defaultDisclosureState;
  }
};
const sumDisplayedMoney = (values: number[]) => values.reduce((sum, value) => sum + Math.round(value * 100), 0) / 100;
const policyScopeLabel = (scopeKey: string) => {
  if (policyScopeLabels[scopeKey]) return policyScopeLabels[scopeKey];
  const employment = /^employment:(\d+)$/.exec(scopeKey);
  return employment ? `Працевлаштування №${employment[1]}` : scopeKey;
};
const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const requestKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);
const messageFrom = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  return fallback;
};

const RestaurantPayrollPage: React.FC = () => {
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  if (changedInAnotherTab)
    return <main className="salary-payroll"><p role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p></main>;
  if (!groupId || !isRestaurantOperatingContext(groupId))
    return <main className="salary-payroll"><p role="alert">Оберіть одну операційну групу для роботи із зарплатою ресторану.</p></main>;
  return <RestaurantPayrollWorkspace key={groupId} groupId={groupId} />;
};

const RestaurantPayrollWorkspace: React.FC<{ groupId: string }> = ({ groupId }) => {
  const [month, setMonth] = useState(currentMonth);
  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [options, setOptions] = useState<PayrollOptions | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [revenueAmount, setRevenueAmount] = useState('0');
  const [revenueNotes, setRevenueNotes] = useState('');
  const [selectedEmploymentId, setSelectedEmploymentId] = useState('');
  const [draftInputs, setDraftInputs] = useState<Record<number, DraftInputs>>({});
  const [policyEmploymentId, setPolicyEmploymentId] = useState('');
  const [policyMethod, setPolicyMethod] = useState<PayrollEmployee['method']>('hourly-coefficient');
  const [policyFixedRate, setPolicyFixedRate] = useState('0');
  const [policyRevenuePercent, setPolicyRevenuePercent] = useState('0');
  const [policyHourlyRate, setPolicyHourlyRate] = useState('');
  const [policyCoefficient, setPolicyCoefficient] = useState('');
  const [salaryRulePosition, setSalaryRulePosition] = useState('');
  const [salaryRuleRate, setSalaryRuleRate] = useState('');
  const [adjustmentEmploymentId, setAdjustmentEmploymentId] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentKey, setAdjustmentKey] = useState('');
  const [disclosures, setDisclosures] = useState(readDisclosureState);
  const hallsDisclosure = useRef<HTMLDetailsElement>(null);
  const mounted = useRef(true);
  const currentMonthRef = useRef(month);
  const requestVersion = useRef(0);
  currentMonthRef.current = month;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const [yearText, monthText] = month.split('-');
  const year = Number(yearText);
  const monthNumber = Number(monthText);

  const load = useCallback(async (active: { current: boolean }) => {
    if (!mounted.current || currentMonthRef.current !== month || !year || monthNumber < 1 || monthNumber > 12) return;
    const version = ++requestVersion.current;
    const isCurrent = () => active.current && mounted.current &&
      currentMonthRef.current === month && requestVersion.current === version;
    setBusy(true);
    setPeriod(null);
    setOptions(null);
    setCanEdit(false);
    setError('');
    try {
      const [periodResponse, optionsResponse, accessResponse] = await Promise.all([
        api.get<PayrollPeriod>(`/restaurant-payroll/${year}/${monthNumber}`),
        api.get<PayrollOptions>('/restaurant-payroll/options', { params: { year, month: monthNumber } }),
        api.get<{ editPermissions: string[] }>('/users/me/access'),
      ]);
      if (!isCurrent()) return;
      const loadedPeriod = periodResponse.data;
      setPeriod(loadedPeriod);
      setOptions(optionsResponse.data);
      setCanEdit(accessResponse.data.editPermissions.includes('page.management.salary'));
      setRevenueAmount(String(optionsResponse.data.revenue?.amount ?? 0));
      setRevenueNotes(optionsResponse.data.revenue?.notes ?? '');
      const employee = loadedPeriod.employees[0]?.employmentId ?? optionsResponse.data.employees[0]?.employmentId;
      setSelectedEmploymentId(employee == null ? '' : String(employee));
      setAdjustmentEmploymentId(loadedPeriod.employees[0]?.employmentId == null ? '' : String(loadedPeriod.employees[0].employmentId));
      setDraftInputs(Object.fromEntries(loadedPeriod.employees.map(item => [
        item.employmentId,
        {
          fixedBonus: String(item.fixedBonus),
          bonusPercent: String(item.bonusPercent),
          fixedPenalty: String(item.fixedPenalty),
          penaltyPercent: String(item.penaltyPercent),
        },
      ])));
    } catch (loadError) {
      if (isCurrent()) setError(messageFrom(loadError, 'Не вдалося завантажити зарплатний період ресторану.'));
    } finally {
      if (isCurrent()) setBusy(false);
    }
  }, [year, monthNumber, month]);

  useEffect(() => {
    const active = { current: true };
    void load(active);
    return () => { active.current = false; };
  }, [load]);

  const reload = async () => {
    await load({ current: true });
  };

  const runAction = async (action: () => Promise<void>, successMessage: string) => {
    const isCurrent = () => mounted.current && currentMonthRef.current === month;
    if (!isCurrent() || busy || !canEdit) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      if (!isCurrent()) return;
      await reload();
      if (isCurrent()) setNotice(successMessage);
    } catch (actionError) {
      if (isCurrent()) setError(messageFrom(actionError, 'Не вдалося виконати дію.'));
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const saveRevenue = (event: FormEvent) => {
    event.preventDefault();
    void runAction(async () => {
      await api.put(`/restaurant-payroll/revenue/${year}/${monthNumber}`, {
        version: options?.revenue?.version ?? 0,
        amount: Number(revenueAmount),
        notes: revenueNotes,
      });
    }, 'Виторг ресторану збережено окремо від інших груп.');
  };

  const savePolicy = (event: FormEvent) => {
    event.preventDefault();
    void runAction(async () => {
      await api.put('/restaurant-payroll/policies', {
        employmentId: policyEmploymentId ? Number(policyEmploymentId) : null,
        effectiveYear: year,
        effectiveMonth: monthNumber,
        calculationMethod: policyMethod,
        fixedMonthlyRate: Number(policyFixedRate),
        revenuePercent: Number(policyRevenuePercent),
        hourlyRateOverride: policyHourlyRate === '' ? null : Number(policyHourlyRate),
        evaluationCoefficientOverride: policyCoefficient === '' ? null : Number(policyCoefficient),
      });
    }, 'Приватне правило зарплати ресторану збережено з вибраного місяця.');
  };

  const saveSalaryRule = (event: FormEvent) => {
    event.preventDefault();
    void runAction(async () => {
      await api.put('/restaurant-payroll/salary-rules', {
        position: salaryRulePosition,
        effectiveYear: year,
        effectiveMonth: monthNumber,
        baseRate: Number(salaryRuleRate),
      });
    }, 'Ставку посади збережено для цього ресторану.');
  };

  const saveDraftInput = (event: FormEvent) => {
    event.preventDefault();
    if (!period?.exists || !selectedEmploymentId) return;
    const values = draftInputs[Number(selectedEmploymentId)] ?? {
      fixedBonus: '0', bonusPercent: '0', fixedPenalty: '0', penaltyPercent: '0',
    };
    void runAction(async () => {
      await api.put(`/restaurant-payroll/${year}/${monthNumber}/employees/${selectedEmploymentId}/adjustments`, {
        version: period.version,
        fixedBonus: Number(values.fixedBonus),
        bonusPercent: Number(values.bonusPercent),
        fixedPenalty: Number(values.fixedPenalty),
        penaltyPercent: Number(values.penaltyPercent),
      });
    }, 'Місячні бонуси та штрафи збережено в чернетці.');
  };

  const transition = (action: 'approve' | 'close') => {
    if (!period?.exists) return;
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}/${action}`, { version: period.version });
    }, action === 'approve' ? 'Зарплату ресторану погоджено та зафіксовано.' : 'Період ресторану закрито.');
  };

  const addAdjustment = (event: FormEvent) => {
    event.preventDefault();
    if (!period?.exists || !adjustmentEmploymentId) return;
    const key = adjustmentKey || requestKey();
    setAdjustmentKey(key);
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}/adjustments`, {
        employmentId: Number(adjustmentEmploymentId),
        amount: Number(adjustmentAmount),
        reason: adjustmentReason,
      }, { headers: { 'Idempotency-Key': key } });
      if (!mounted.current || currentMonthRef.current !== month) return;
      setAdjustmentAmount('');
      setAdjustmentReason('');
      setAdjustmentKey('');
    }, 'Коригування додано окремим записом.');
  };

  const createDraft = () => {
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}`, {});
    }, 'Чернетку створено. Перевірте розрахунок перед погодженням.');
  };

  const statusText = period?.status === 'approved' ? 'Погоджено' :
    period?.status === 'closed' ? 'Закрито' : 'Чернетка';
  const persistDisclosure = (key: DisclosureKey, open: boolean) => {
    setDisclosures(previous => {
      const next = { ...previous, [key]: open };
      localStorage.setItem(DISCLOSURE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };
  const onDisclosureToggle = (key: DisclosureKey) => (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (disclosures[key] !== event.currentTarget.open) persistDisclosure(key, event.currentTarget.open);
  };
  const revealHalls = () => {
    const section = hallsDisclosure.current;
    if (!section) return;
    section.open = true;
    persistDisclosure('halls', true);
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <main className="salary-payroll salary-payroll--restaurant">
      <header className="salary-payroll__header">
        <div>
          <h1>Зарплата та виторг ресторану</h1>
          <p>Дані належать лише вибраній операційній групі. Персонал, виторг, правила і періоди інших ресторанів не використовуються.</p>
        </div>
        <label>Місяць<input aria-label="Місяць ресторанної зарплати" type="month" value={month} onChange={event => setMonth(event.target.value)} /></label>
      </header>
      {error && <p role="alert" className="salary-payroll__error">{error}</p>}
      {notice && <p role="status" className="salary-payroll__notice">{notice}</p>}
      {busy && <p role="status">Оновлення ресторанного періоду…</p>}
      {period && options && period.year === year && period.month === monthNumber && <>
        <section className="salary-payroll__summary">
          <p><strong>Статус:</strong> {statusText}</p>
          <p><strong>Власний виторг:</strong> {currency.format(period.monthlyRevenue)}</p>
          <p><strong>Нараховано:</strong> {currency.format(period.total)}</p>
          {period.salaryFundPercent > 0 && <>
            <p><strong>Фонд:</strong> {currency.format(period.salaryFund)} ({formatRestaurantPayrollDecimal(period.salaryFundPercent, 2)}%)</p>
            <p><strong>Зважені години:</strong> {formatRestaurantPayrollDecimal(period.totalSalaryHours, 2)} · {currency.format(period.hourRate)} / год.</p>
          </>}
          {period.approvedByUsername && <p>Погодив(-ла): {period.approvedByUsername}</p>}
          {period.closedByUsername && <p>Закрив(-ла): {period.closedByUsername}</p>}
        </section>

        {period.issues.length > 0 && <section className="salary-payroll__issues" aria-label="Проблеми розрахунку">
          <h2>Потрібно виправити перед погодженням</h2>
          <ul>{period.issues.map((issue, index) => <li key={`${issue}-${index}`}>{issue}</li>)}</ul>
        </section>}
        <div className="salary-payroll__table-wrap">
          <table>
            <thead><tr><th>Працівник ресторану</th><th>Підрозділ / посада</th><th>Години</th><th>Ставка / коефіцієнт</th><th>База</th><th>Бонус</th><th>Штраф</th><th>Разом</th></tr></thead>
            <tbody>{period.employees.map(item => <tr key={item.employmentId}>
              <td>{item.employeeName}</td><td>{item.departmentName || '—'} / {item.position || '—'}</td>
              <td>{formatRestaurantPayrollDecimal(item.workedHours, 2)}{item.method === 'revenue-weighted-hours' || item.method === 'halls-fund' ? ` / ${formatRestaurantPayrollDecimal(item.salaryHours, 2)}` : ''}</td>
              <td>{item.method === 'hourly-coefficient' ? `${currency.format(item.hourlyRate)} × ${formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)}` :
                item.method === 'halls-fund' ? `HALLS · коеф. ${formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)}` :
                item.method === 'revenue-weighted-hours' ? `коеф. ${formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)}` :
                  `${currency.format(item.fixedMonthlyRate)} + ${formatRestaurantPayrollDecimal(item.revenuePercent, 2)}%`}</td>
              <td>{currency.format(item.baseSalary)}</td><td>{currency.format(item.bonus)}</td><td>{currency.format(item.penalty)}</td><td>{currency.format(item.amount)}</td>
            </tr>)}
            {!period.employees.length && <tr><td colSpan={8}>За цей період немає працівників для розрахунку.</td></tr>}
              <tr className="salary-payroll__table-total"><th scope="row">Разом</th><td>—</td><td>—</td><td>—</td>
                <td>{currency.format(sumDisplayedMoney(period.employees.map(item => item.baseSalary)))}</td>
                <td>{currency.format(sumDisplayedMoney(period.employees.map(item => item.bonus)))}</td>
                <td>{currency.format(sumDisplayedMoney(period.employees.map(item => item.penalty)))}</td>
                <td>{currency.format(period.total)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="salary-payroll__actions" aria-label="Дії із зарплатним періодом">
          {canEdit && !period.exists && <button type="button" disabled={busy} onClick={createDraft}>Створити чернетку</button>}
          {canEdit && period.exists && period.status === 'draft' && <>
            <button type="button" disabled={busy || period.issues.length > 0 || !period.employees.length} onClick={() => transition('approve')}>Погодити й зафіксувати</button>
            {(busy || period.issues.length > 0 || !period.employees.length) && <span className="salary-payroll__approval-reason" role="status">
              {busy ? 'Триває збереження' : period.issues.length > 0 ? `Є ${period.issues.length} проблем` : 'Немає працівників у розрахунку'}
            </span>}
            <button type="button" className="salary-payroll__secondary-action" aria-controls="restaurant-payroll-halls" onClick={revealHalls}>Перевірити фонд HALLS</button>
          </>}
          {canEdit && period.status === 'approved' && <button type="button" disabled={busy} onClick={() => transition('close')}>Закрити період</button>}
        </div>

        <details ref={hallsDisclosure} id="restaurant-payroll-halls" className="salary-payroll__disclosure salary-payroll__disclosure--halls"
          open={disclosures.halls} onToggle={onDisclosureToggle('halls')}>
          <summary>Фонд HALLS цієї групи — перевірка й налаштування</summary>
          <RestaurantHallsPayrollPanel groupId={groupId} year={year} month={monthNumber} version={period.version}
            exists={period.exists} status={period.status} canEdit={canEdit} busy={busy}
            employees={options.employees} review={period.halls} runAction={runAction} />
        </details>

        <details className="salary-payroll__disclosure" open={disclosures.revenue} onToggle={onDisclosureToggle('revenue')}>
          <summary>Місячний виторг ресторану</summary>
          <section className="salary-payroll__policy-settings">
          <p>Виторг є приватним записом вибраної групи; запис HALLS або іншого ресторану не є резервним джерелом.</p>
          {canEdit && period.status === 'draft' && <form onSubmit={saveRevenue}>
            <label>Виторг, ₴<input required type="number" min="0" step="0.01" value={revenueAmount} onChange={event => setRevenueAmount(event.target.value)} /></label>
            <label>Примітка<input maxLength={2000} value={revenueNotes} onChange={event => setRevenueNotes(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Зберегти виторг</button>
          </form>}

          </section>
        </details>

        <details className="salary-payroll__disclosure" open={disclosures.policies} onToggle={onDisclosureToggle('policies')}>
          <summary>Правила нарахування й ставки</summary>
          <section className="salary-payroll__policy-settings">
          <p>Глобальне правило ресторану або точкове правило працевлаштування; коефіцієнт оцінювання й табель беруться лише з приватних HR-записів цієї групи.</p>
          {canEdit && period.status === 'draft' && <form onSubmit={savePolicy}>
            <label>Застосувати до
              <select value={policyEmploymentId} onChange={event => setPolicyEmploymentId(event.target.value)}>
                <option value="">Усього ресторану</option>
                {options.employees.map(employee => <option key={employee.employmentId} value={employee.employmentId}>{employee.fullName}</option>)}
              </select>
            </label>
            <label>Метод
              <select value={policyMethod} onChange={event => setPolicyMethod(event.target.value as PayrollEmployee['method'])}>
                <option value="hourly-coefficient">Години × приватна ставка × коефіцієнт</option>
                <option value="fixed-plus-revenue">Фіксована сума + частка виторгу</option>
                {!policyEmploymentId && <option value="revenue-weighted-hours">Власний фонд від виторгу ÷ зважені години</option>}
              </select>
            </label>
            {policyMethod === 'fixed-plus-revenue' && <>
              <label>Фіксована сума, ₴<input type="number" min="0" step="0.01" value={policyFixedRate} onChange={event => setPolicyFixedRate(event.target.value)} /></label>
              <label>Відсоток виторгу<input type="number" min="0" max="100" step="0.01" value={policyRevenuePercent} onChange={event => setPolicyRevenuePercent(event.target.value)} /></label>
            </>}
            {policyMethod === 'revenue-weighted-hours' && <label>Відсоток фонду<input type="number" min="0" max="100" step="0.01" value={policyRevenuePercent} onChange={event => setPolicyRevenuePercent(event.target.value)} /></label>}
            {policyMethod === 'hourly-coefficient' && <label>Ставка, ₴ (порожньо — приватна ставка/посадове правило)<input type="number" min="0" step="0.01" value={policyHourlyRate} onChange={event => setPolicyHourlyRate(event.target.value)} /></label>}
            <label>Коефіцієнт (порожньо — локальне HR-оцінювання)<input type="number" min="0" step="0.000001" value={policyCoefficient} onChange={event => setPolicyCoefficient(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Зберегти правило з {String(monthNumber).padStart(2, '0')}.{year}</button>
          </form>}
          {canEdit && period.status === 'draft' && <form onSubmit={saveSalaryRule}>
            <h3 className="salary-payroll__form-heading">Погодинна ставка за посадою</h3>
            <label>Посада<input required maxLength={200} value={salaryRulePosition} onChange={event => setSalaryRulePosition(event.target.value)} /></label>
            <label>Ставка, ₴<input required type="number" min="0" step="0.01" value={salaryRuleRate} onChange={event => setSalaryRuleRate(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Зберегти ставку з {String(monthNumber).padStart(2, '0')}.{year}</button>
          </form>}
          <div className="salary-payroll__policy-list">
            <h3>Правила ресторану</h3>
            {options.policies.map(item => <p key={item.id}>{policyScopeLabel(item.scopeKey)} · {String(item.effectiveMonth).padStart(2, '0')}.{item.effectiveYear} · v{item.revision} · {calculationMethodLabels[item.calculationMethod] ?? item.calculationMethod}</p>)}
            {options.salaryRules.map(item => <p key={item.id}>{item.position}: {currency.format(item.baseRate)} · {String(item.effectiveMonth).padStart(2, '0')}.{item.effectiveYear} · v{item.revision}</p>)}
          </div>
          </section>
        </details>

        <details className="salary-payroll__disclosure" open={disclosures.bonuses} onToggle={onDisclosureToggle('bonuses')}>
          <summary>Бонуси та штрафи за місяць</summary>
          <section className="salary-payroll__policy-settings">
          {canEdit && period.exists && period.status === 'draft' && <>
            <form onSubmit={saveDraftInput}>
              <label>Працевлаштування<select required value={selectedEmploymentId} onChange={event => setSelectedEmploymentId(event.target.value)}>
                {period.employees.map(item => <option key={item.employmentId} value={item.employmentId}>{item.employeeName}</option>)}
              </select></label>
              {(['fixedBonus', 'bonusPercent', 'fixedPenalty', 'penaltyPercent'] as const).map(field => <label key={field}>
                {field === 'fixedBonus' ? 'Фіксований бонус, ₴' : field === 'bonusPercent' ? 'Бонус, %' : field === 'fixedPenalty' ? 'Фіксований штраф, ₴' : 'Штраф, %'}
                <input type="number" min="0" step="0.01"
                  value={draftInputs[Number(selectedEmploymentId)]?.[field] ?? '0'}
                  onChange={event => setDraftInputs(previous => ({
                    ...previous,
                    [Number(selectedEmploymentId)]: {
                      ...(previous[Number(selectedEmploymentId)] ?? { fixedBonus: '0', bonusPercent: '0', fixedPenalty: '0', penaltyPercent: '0' }),
                      [field]: event.target.value,
                    },
                  }))} />
              </label>)}
              <button type="submit" disabled={busy || !selectedEmploymentId}>Зберегти для місяця</button>
            </form>
          </>}
          </section>
        </details>

        {(period.status === 'closed' || period.legacyHallsHistory || period.employees.some(item => item.adjustments.length > 0)) && <details className="salary-payroll__disclosure"
          open={disclosures.adjustments} onToggle={onDisclosureToggle('adjustments')}>
          <summary>Коригування та архівні записи</summary>
          <section className="salary-payroll__policy-settings">
            {canEdit && period.status === 'closed' && <form className="salary-payroll__adjustment" onSubmit={addAdjustment}>
              <h2>Коригування після закриття</h2>
              <label>Працевлаштування<select required value={adjustmentEmploymentId} onChange={event => { setAdjustmentEmploymentId(event.target.value); setAdjustmentKey(''); }}>
                {period.employees.map(item => <option key={item.employmentId} value={item.employmentId}>{item.employeeName}</option>)}
              </select></label>
              <label>Сума (+ / −)<input required type="number" step="0.01" value={adjustmentAmount} onChange={event => { setAdjustmentAmount(event.target.value); setAdjustmentKey(''); }} /></label>
              <label>Причина<input required maxLength={1000} value={adjustmentReason} onChange={event => { setAdjustmentReason(event.target.value); setAdjustmentKey(''); }} /></label>
              <button type="submit" disabled={busy || !adjustmentEmploymentId}>Додати коригування</button>
            </form>}
            {period.legacyHallsHistory && <section className="salary-payroll__adjustments">
              <h2>Архівна зарплата HALLS</h2>
              <p>Незмінний історичний знімок; нові ресторанні розрахунки його не читають як джерело.</p>
              <p>{period.legacyHallsHistory.departmentNameSnapshot} · виторг замовлень {currency.format(period.legacyHallsHistory.orderRevenueSnapshot)} · фонд {currency.format(period.legacyHallsHistory.totalPoolSnapshot)}</p>
              {period.legacyHallsHistory.employees.map((employee, index) => <p key={`${employee.employeeName}-${index}`}>{employee.employeeName}: {currency.format(employee.amount)}</p>)}
            </section>}
            {period.employees.some(item => item.adjustments.length > 0) && <section className="salary-payroll__adjustments">
              <h2>Окремі коригування</h2>
              {period.employees.flatMap(item => item.adjustments.map((adjustment, index) =>
                <p key={`${item.employmentId}-${index}`}>{item.employeeName}: {currency.format(adjustment.amount)} — {adjustment.reason} ({adjustment.createdByUsername})</p>))}
            </section>}
          </section>
        </details>}
      </>}
    </main>
  );
};

export default RestaurantPayrollPage;
